import type { AuthenticatedUser } from '@ecom/contracts';

/**
 * Token storage for the admin SPA.
 *
 * A static SPA has no server of its own (ADR-008), so it cannot use httpOnly
 * cookies the way the storefront does — the tokens have to live somewhere the
 * browser can read. That is a real, accepted tradeoff, and these are the
 * mitigations it rests on:
 *
 * - the access token is short-lived and kept in memory only, so it never
 *   survives a tab close and never sits in storage for a script to find;
 * - only the refresh token is persisted, and it **rotates on every use with
 *   reuse detection** — a stolen copy, once used, revokes the entire family
 *   server-side (docs/03-Backend/Authentication.md);
 * - the admin never renders user-authored HTML, which is the usual way a token
 *   in storage gets stolen in the first place.
 */

const REFRESH_TOKEN_KEY = 'ecom_admin_rt';

/** In memory on purpose — see the note above. */
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The session
    // then lasts until the tab closes, which is degraded but still usable.
    return null;
  }
}

export function setRefreshToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(REFRESH_TOKEN_KEY);
    else localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {
    // Ignored for the same reason as above.
  }
}

export function clearSession(): void {
  setAccessToken(null);
  setRefreshToken(null);
}

/** Staff see operations; only an owner may touch money and settings. */
export function isOwner(user: AuthenticatedUser | null): boolean {
  return user?.role === 'OWNER';
}

/**
 * Permission check mirroring the API's guard.
 *
 * Hiding a button is never the security boundary — the API checks every call
 * (07-Admin rules). This exists so staff are not shown actions that will fail.
 */
export function can(user: AuthenticatedUser | null, permission: string): boolean {
  if (!user) return false;
  if (user.role === 'OWNER') return true;

  return user.permissions.includes(permission);
}
